import { useState, useEffect, useMemo } from 'react';
import { Event } from 'nostr-tools';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS } from '../config/constants';
import { useAdmin } from '../contexts/AdminContext';

export function useReactions(eventId: string | null) {
    const [rawEvents, setRawEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { settings } = useAdmin();

    useEffect(() => {
        if (!eventId) return;

        const pool = getNostrPool();
        setIsLoading(true);
        setRawEvents([]);

        const allRelays = [...new Set([...RELAYS, ...settings.customRelays])];

        const filter = {
            kinds: [7],
            '#e': [eventId]
        };

        const sub = pool.subscribeMany(allRelays, filter, {
            onevent(event: Event) {
                setRawEvents(prev => {
                    const exists = prev.some(e => e.id === event.id);
                    if (exists) return prev;
                    return [...prev, event];
                });
            },
            oneose() {
                setIsLoading(false);
            }
        });

        const timeout = setTimeout(() => {
            setIsLoading(false);
        }, 5000);

        return () => {
            sub.close();
            clearTimeout(timeout);
        };
    }, [eventId, settings.customRelays]);

    const stats = useMemo(() => {
        // 1. Deduplicate by pubkey (Keep latest created_at)
        const latestVotes = new Map<string, Event>(); // pubkey -> event

        rawEvents.forEach(event => {
            const existing = latestVotes.get(event.pubkey);
            if (!existing || event.created_at > existing.created_at) {
                latestVotes.set(event.pubkey, event);
            }
        });

        // 2. Classify
        let upvotes = 0;
        let downvotes = 0;
        const userVotes: Record<string, { content: string, id: string }> = {};

        latestVotes.forEach((event) => {
            const content = event.content;

            // Normalize content for comparison
            // Upvotes: "+", "👍", "❤️", "🤙", or empty/undefined
            // Downvotes: "-", "👎"
            const isDownvote = content === '-' || content === '👎';
            const isUpvote = !isDownvote && (content === '+' || content === '👍' || content === '❤️' || content === '🤙' || !content || content === '');

            if (isDownvote) downvotes++;
            else if (isUpvote) upvotes++;

            // Populate userVotes map for UI state (e.g. valid 'sen' vote)
            // We store the normalized type for easier consumption in EntryCard
            userVotes[event.pubkey] = {
                content: isDownvote ? '-' : '+',
                id: event.id
            };
        });

        return {
            upvotes,
            downvotes,
            userVotes,
            isLoading // Pass loading state here for convenience if needed
        };
    }, [rawEvents]);

    return { ...stats, isLoading };
}
